/**
 * Header is a React component that displays the header of the application.
 * It includes a title and a logout button.
 * When the logout button is clicked, the user's authentication is cleared and they are navigated to the "/login" route.
 * The component uses the useNavigate hook from react-router-dom to handle navigation.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../../App.css";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showBetaForm, setShowBetaForm] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const apiEndpoint = process.env.REACT_APP_API_ENDPOINT;
  const isLoginPage = location.pathname === "/login";
  const showBrand = location.pathname === "/" || isLoginPage;

  useEffect(() => {
    let isMounted = true;
    const fetchStatus = async () => {
      try {
        const token = localStorage.getItem("accessToken");
        if (!token) return;
        const parsedToken = typeof token === "string" ? JSON.parse(token) : token;
        const tokenString =
          typeof parsedToken === "object"
            ? JSON.stringify(parsedToken)
            : parsedToken;
        const response = await fetch(`${apiEndpoint}/workspace/projects`, {
          headers: { Authorization: `Bearer ${tokenString}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted) {
          setUserStatus(data?.status || null);
        }
      } catch (error) {
        console.error("Failed to load user status:", error);
      }
    };

    fetchStatus();
    return () => {
      isMounted = false;
    };
  }, [apiEndpoint]);

  const handleLogoutClick = () => {
    // Clear authentication token from localStorage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userStatus');
    localStorage.removeItem('demoTourActive');
    localStorage.removeItem('demoTourStep');

    // Clear any other auth-related data that might be stored
    // You can add more localStorage.removeItem() calls here if needed
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("mdscaleway_")) {
        localStorage.removeItem(key);
      }
    });
    
    console.log("User logged out successfully");
    
    // Navigate to login page
    navigate("/login");
  };

  return (
    <div className="header">
      {showBrand && (
        <a
          className="sa-logo-link header-logo"
          href="https://www.simatomic.com"
          aria-label="SimAtomic landing page"
        >
          <img
            className="sa-logo"
            src={`${process.env.PUBLIC_URL}/logo2.png`}
            alt="SimAtomic Logo"
          />
        </a>
      )}
      {userStatus !== "user" && userStatus !== "admin" && !isLoginPage && (
        <>
          <button
            type="button"
            className="beta-button"
            onClick={() => setShowBetaForm(true)}
          >
            Signup for Beta
          </button>
          {showBetaForm && (
            <div
              className="sa-modal-overlay"
              onClick={() => setShowBetaForm(false)}
            >
              <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <div className="sa-modal-header">
                  <h3>Signup for Beta</h3>
                  <button
                    type="button"
                    className="sa-modal-close"
                    onClick={() => setShowBetaForm(false)}
                  >
                    ×
                  </button>
                </div>
                <form
                  action="https://formspree.io/f/xojjnrql"
                  method="POST"
                  target="_blank"
                >
                  <div className="sa-modal-body">
                    <p className="sa-modal-hint">
                      Signup includes 100 free credits to test out the platform.
                    </p>
                    <label className="sa-modal-label">Name</label>
                    <input
                      className="sa-modal-input"
                      name="name"
                      placeholder="Full name"
                      required
                    />
                    <label className="sa-modal-label">Email</label>
                    <input
                      className="sa-modal-input"
                      type="email"
                      name="email"
                      placeholder="name@institute.edu"
                      required
                    />
                    <label className="sa-modal-label">What Using It For</label>
                    <textarea
                      className="sa-modal-textarea"
                      name="use_case"
                      placeholder="Briefly describe your use case"
                      rows={4}
                      required
                    />
                  </div>
                  <div className="sa-modal-footer">
                    <button
                      type="button"
                      className="sa-btn sa-btn--outline"
                      onClick={() => setShowBetaForm(false)}
                    >
                      Cancel
                    </button>
                    <button type="submit" className="sa-btn sa-btn--primary">
                      Sign Up <span className="sa-btn-credits">(100 GPU Credits)</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </>
      )}
      {!isLoginPage && (
        <button className="right-button" onClick={handleLogoutClick}>
          Logout
        </button>
      )}
    </div>
  );
};

export default Header;
